import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { addReturnTagFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  AddReturnTagState,
  isAddReturnTagStepValid,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import TagIcon from "@redotech/redo-web/icon-old/tag.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

/**
 * Returns the appropriate tag type name based on the flow type.
 * Used for displaying labels like "Return tags", "Claim tags", or "Warranty tags".
 */
export function getTagTypeName(flowType: FlowType | undefined): string {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (flowType) {
    case FlowType.CLAIM:
    case FlowType.FINALIZE_CLAIM:
      return "Claim";
    case FlowType.WARRANTY:
    case FlowType.WARRANTY_REGISTRATION:
    case FlowType.FINALIZE_WARRANTY:
      return "Warranty";
    default:
      return "Return";
  }
}

export const ADD_RETURN_TAG = (
  Details: React.ComponentType<StepTypeDetailsProps<AddReturnTagState>>,
  title = STEP_TYPE_NAMES[ModelStep.ADD_RETURN_TAG],
): StepType<AddReturnTagState, ModelStep.AddReturnTag> => ({
  Details: (props: StepTypeDetailsProps<AddReturnTagState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title,
  valid(state, flowType) {
    return isAddReturnTagStepValid(state, flowType).valid;
  },
  Icon: TagIcon,
  iconColor: "success",
  description(state, _team, flowType) {
    if (state.tags.length > 0) {
      return t("Add tags: {{tags}}", {
        tags: state.tags.map((tag) => tag.name).join(", "),
      });
    }
    switch (getTagTypeName(flowType)) {
      case "Claim":
        return t("Add tags to the claim");
      case "Warranty":
        return t("Add tags to the warranty");
      default:
        return t("Add tags to the return");
    }
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, tags: [] },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Add Return Tag step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.ADD_RETURN_TAG,
      tags: state.tags,
      next: state.next ? id(state.next) : undefined,
    };
  },
  fromModel(model, id) {
    return addReturnTagFromModel(model, id);
  },
});
